import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileUp,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Reveal } from "@/components/util/Reveal";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { money, listMoney, usdApprox, useCurrency } from "@/lib/currency";
import { ChargeDisclosure } from "@/components/util/ChargeDisclosure";
import { useSalesTax } from "@/lib/tax";
import { useCart } from "@/contexts/CartContext";
import { ServiceHelpLinks } from "@/components/util/ServiceHelpLinks";

// The three "I already have a logo" service paths — pulled from the
// service.standalone_items collection so pricing / description remain
// CMS-editable. The `key` is the URL-safe canonical name we thread into
// order metadata as `branding_service_path`.
const EXISTING_LOGO_ITEMS = [
  {
    key: "vectorization",
    itemName: "Logo Vectorization / Recreation",
    testId: "existing-logo-vectorization",
    heading: "Recreate It",
    tag: "Keep my logo the same",
    body: "Professionally recreate your existing logo or supplied sketch as clean, scalable vector artwork. The design itself stays the same.",
  },
  {
    key: "refresh",
    itemName: "Logo Refresh / Modernization",
    testId: "existing-logo-refresh",
    heading: "Refresh It",
    tag: "Improve my existing logo",
    body: "Refine and modernize your existing logo while preserving its recognizable identity. Ideal for established brands that need a cleaner, more current look without starting over.",
  },
  {
    key: "bi_existing_logo",
    itemName: "Brand Identity — Existing Logo",
    testId: "existing-logo-brand-identity",
    heading: "Build My Brand Around It",
    tag: "I need the identity, not a new logo",
    body: "A complete brand identity system built around your existing logo — brand voice, color palette, typography, basic usage guidelines, business card, letterhead and email signature.",
  },
];

// Contextual "Add Brand Identity — Existing Logo" upsell offered after
// the customer picks Refresh or Vectorization. Never shown when the
// customer already picked BI-Existing-Logo directly.
const BI_EXISTING_UPSELL_KEY = "bi_existing_logo_addition";

const scopeSafetyCopy =
  "If the requested work falls outside the selected service, Ur Designs will confirm the appropriate option and any price difference before work begins.";

export const ExistingLogoConfigurator = () => {
  useCurrency(); // re-render prices on display-currency change
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { addItem } = useCart();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [addBiUpsell, setAddBiUpsell] = useState(false);
  // Batch 3 — minimal business context, ONLY relevant to
  // `bi_existing_logo` (Build My Brand Around It). Persisted verbatim
  // into cart line `meta.project_brief`; cleared when the customer
  // switches away from that path (mirrors the addBiUpsell reset in
  // the sub-choice card onClick).
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [upload, setUpload] = useState(null); // {url, filename, size, mime}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services/logo-brand-identity")
      .then((r) => setService(r.data))
      .catch(() => toast.error("Could not load Branding services. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  // If the customer landed here via a specific ?item= deep-link (e.g. from
  // a marketing link), preselect that service.
  useEffect(() => {
    const preselect = params.get("item");
    if (preselect && EXISTING_LOGO_ITEMS.some((e) => e.key === preselect)) {
      setSelectedKey(preselect);
    }
  }, [params]);

  const itemsByName = useMemo(() => {
    const map = {};
    (service?.standalone_items || []).forEach((it) => {
      map[(it.name || "").trim()] = it;
    });
    return map;
  }, [service]);

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    const meta = EXISTING_LOGO_ITEMS.find((e) => e.key === selectedKey);
    if (!meta) return null;
    const dbItem = itemsByName[meta.itemName];
    if (!dbItem) return null;
    return { ...meta, item: dbItem };
  }, [selectedKey, itemsByName]);

  const biExistingItem = itemsByName["Brand Identity — Existing Logo"];
  const canOfferBiUpsell =
    selected && (selected.key === "refresh" || selected.key === "vectorization") && !!biExistingItem;

  const total = useMemo(() => {
    if (!selected) return 0;
    let n = Number(selected.item.price) || 0;
    if (canOfferBiUpsell && addBiUpsell) n += Number(biExistingItem?.price) || 0;
    return n;
  }, [selected, canOfferBiUpsell, addBiUpsell, biExistingItem]);
  // Display-only US$ list-price sum (AWG `total` stays authoritative).
  const totalUsd = useMemo(() => {
    if (!selected) return undefined;
    let n = Number(selected.item.price_usd) || 0;
    if (canOfferBiUpsell && addBiUpsell) n += Number(biExistingItem?.price_usd) || 0;
    const expected = Number(selected.item.price) > 0 || (canOfferBiUpsell && addBiUpsell && Number(biExistingItem?.price) > 0);
    return n > 0 || !expected ? n || undefined : undefined;
  }, [selected, canOfferBiUpsell, addBiUpsell, biExistingItem]);
  // Site-wide tax — shared source with Checkout via /api/settings.
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();
  const { taxAmount, total: totalWithTax } = computeTax(total);

  const isBiExisting = selected?.key === "bi_existing_logo";
  const trimmedBusinessName = businessName.trim();
  const trimmedBusinessDescription = businessDescription.trim();
  const canProceed =
    !!selected && !!upload && (!isBiExisting || !!trimmedBusinessName);

  const onFileChosen = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/project-brief/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload({
        url: data.url,
        filename: file.name,
        size: data.size,
        mime: data.mime,
      });
      toast.success("Logo uploaded — you're ready to continue.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearUpload = () => setUpload(null);

  const proceed = () => {
    if (!canProceed || !selected) return;
    setSubmitting(true);
    // Primary line — the chosen existing-logo service.
    addItem({
      kind: "service_package",
      name: selected.item.name,
      price: Number(selected.item.price) || 0,
      qty: 1,
      // P0 canonical fields — server resolves against logo-brand-identity
      // service catalog using item_id.
      service_slug: "logo-brand-identity",
      line_type: "standalone",
      item_code: selected.item.item_code || "",
      item_id: selected.item.id,
      quantity: 1,
      meta: {
        service_slug: "logo-brand-identity",
        service_name: "Logo & Brand Identity",
        branding_entry_path: "existing_logo",
        branding_service_path: selected.key,
        existing_logo_required: true,
        existing_logo_upload: {
          url: upload.url,
          filename: upload.filename,
          mime: upload.mime,
          size: upload.size,
        },
        // Batch 3 — minimal production context for the
        // "Build My Brand Around It" path only. Description omitted
        // when the customer left it blank so the payload stays clean.
        ...(isBiExisting
          ? {
              project_brief: {
                business_name: trimmedBusinessName,
                ...(trimmedBusinessDescription
                  ? { business_description: trimmedBusinessDescription }
                  : {}),
              },
            }
          : {}),
        item_id: selected.item.id,
      },
    });
    // Contextual add — BI-Existing-Logo appended as its own line so
    // production sees both service paths explicitly (Refresh + BI-Existing
    // together = 2400; Vectorization + BI-Existing = 1950). Do NOT collapse
    // into the standard Brand Identity tier — the scope IS different.
    if (canOfferBiUpsell && addBiUpsell && biExistingItem) {
      addItem({
        kind: "service_package",
        name: biExistingItem.name,
        price: Number(biExistingItem.price) || 0,
        qty: 1,
        service_slug: "logo-brand-identity",
        line_type: "standalone",
        item_code: biExistingItem.item_code || "",
        item_id: biExistingItem.id,
        quantity: 1,
        meta: {
          service_slug: "logo-brand-identity",
          service_name: "Logo & Brand Identity",
          branding_entry_path: "existing_logo",
          branding_service_path: "bi_existing_logo",
          existing_logo_required: true,
          contextual_bi_added: true,
          added_alongside: selected.key,
          // Share the same uploaded logo — no need to re-upload.
          existing_logo_upload: {
            url: upload.url,
            filename: upload.filename,
            mime: upload.mime,
            size: upload.size,
          },
          item_id: biExistingItem.id,
        },
      });
    }
    setSubmitting(false);
    toast.success("Added to your cart.");
    navigate("/checkout");
  };

  if (loading) {
    return <BrandedLoader testid="existing-logo-loading" />;
  }

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <button
          type="button"
          onClick={() => navigate("/services/logo-brand-identity")}
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#A3A3A3] hover:text-[#FF6600] mb-6"
          data-testid="existing-logo-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <Reveal>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">
            I already have a logo
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-3">
            What would you like to do with your logo?
          </h1>
          <p className="text-[#A3A3A3] mb-8 text-sm sm:text-base max-w-2xl">
            Three clear options — pick the one that matches what you actually need.
          </p>
        </Reveal>

        {/* Step 1 — Sub-choice */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch mb-10">
          {EXISTING_LOGO_ITEMS.map((opt, i) => {
            const db = itemsByName[opt.itemName];
            const price = db?.price;
            const isSel = selectedKey === opt.key;
            return (
              <Reveal key={opt.key} delay={i * 70} className="h-full">
                <button
                  type="button"
                  data-testid={opt.testId}
                  onClick={() => {
                    // Feb 2026 owner directive — the three "I already
                    // have a logo" cards are a mutually-exclusive
                    // group where deselect is a valid state. Clicking
                    // an unselected card selects it (replacing any
                    // prior choice). Clicking the currently-selected
                    // card again clears the group so:
                    //   • `selectedKey` → null
                    //   • `total` returns 0 (via `if (!selected)`)
                    //   • `canProceed` disables Continue (via `!!selected`)
                    //   • the BI-existing upsell toggle + any brand-
                    //     context fields typed under the bi_existing
                    //     branch reset so no stale data reaches the
                    //     cart payload on re-select.
                    // The already-uploaded logo file is intentionally
                    // preserved (spec §6 — selection state and
                    // uploaded reference artwork are separate
                    // concerns).
                    const isDeselect = selectedKey === opt.key;
                    setSelectedKey(isDeselect ? null : opt.key);
                    setAddBiUpsell(false);
                    // Clear brand-context fields when switching away
                    // from bi_existing OR fully deselecting.
                    if (isDeselect || opt.key !== "bi_existing_logo") {
                      setBusinessName("");
                      setBusinessDescription("");
                    }
                  }}
                  aria-pressed={isSel}
                  className={`h-full w-full text-left bg-[#151515] rounded-2xl p-6 flex flex-col border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6600]/40 ${
                    isSel ? "border-[#FF6600]" : "border-[#262626] hover:border-[#FF6600]/40"
                  }`}
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3] mb-2">
                    {opt.tag}
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2 leading-tight">{opt.heading}</h3>
                  <p className="text-sm text-[#D1D1D1] leading-relaxed mb-4">{opt.body}</p>
                  <div className="mt-auto flex items-end justify-between">
                    <div className="font-display font-black text-2xl">
                      {price != null ? listMoney(price) : "—"}
                    </div>
                    <span
                      className={`w-6 h-6 grid place-items-center rounded-full border ${
                        isSel ? "bg-[#FF6600] border-[#FF6600] text-white" : "border-[#3A3A3A] text-transparent"
                      }`}
                      aria-hidden
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>

        {selected && (
          <>
            {/* Step 2 — Mandatory upload */}
            <Reveal>
              <div
                className="bg-[#151515] rounded-2xl border border-[#262626] p-6 sm:p-8 mb-8"
                data-testid="existing-logo-upload-block"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 grid place-items-center rounded-xl bg-[#1F1F1F] border border-[#262626] text-[#FF6600] shrink-0">
                    <FileUp className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display font-bold text-xl mb-1">Upload Your Existing Logo</h2>
                    <p className="text-sm text-[#D1D1D1] mb-4">
                      Upload the logo, sketch or artwork you currently have so we can confirm the selected service is the right fit.
                      <span className="block text-xs text-[#A3A3A3] mt-1">
                        Accepted: JPG · PNG · PDF · scan · photo · sketch (max 10 MB).
                      </span>
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.webp,image/jpeg,image/png,image/pdf,image/webp,application/pdf"
                      className="hidden"
                      onChange={(e) => onFileChosen(e.target.files?.[0])}
                      data-testid="existing-logo-file-input"
                    />
                    {!upload ? (
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-[#FF6600] hover:bg-[#e65a00] text-white font-bold"
                        data-testid="existing-logo-upload-button"
                      >
                        {uploading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> Choose File</>
                        )}
                      </Button>
                    ) : (
                      <div
                        className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2"
                        data-testid="existing-logo-uploaded"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{upload.filename}</div>
                          <div className="text-[11px] text-[#A3A3A3]">
                            {(upload.size / 1024).toFixed(0)} KB · {upload.mime}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearUpload}
                          className="text-[#A3A3A3] hover:text-red-400"
                          data-testid="existing-logo-remove"
                          aria-label="Remove uploaded file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <p className="text-[11px] text-[#7A7A7A] mt-4 leading-relaxed">
                      {scopeSafetyCopy}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Step 2b — Business context (Batch 3). Only for the
                Build My Brand Around It path. Two lightweight fields:
                one required name, one optional description. Values
                persist into meta.project_brief. */}
            {isBiExisting && (
              <Reveal>
                <div
                  className="bg-[#151515] rounded-2xl border border-[#262626] p-6 sm:p-8 mb-8"
                  data-testid="existing-logo-brief-block"
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">
                    A little context
                  </div>
                  <h2 className="font-display font-bold text-xl mb-1">
                    Tell us about the business
                  </h2>
                  <p className="text-sm text-[#A3A3A3] mb-5">
                    Just enough for our team to build the identity around what already exists.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="bi-business-name"
                        className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#D1D1D1] mb-2"
                      >
                        Business / Brand Name <span className="text-[#FF6600]">*</span>
                      </label>
                      <input
                        id="bi-business-name"
                        type="text"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        placeholder="e.g. Aruba Palm Café"
                        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF6600] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#5A5A5A] outline-none transition-colors"
                        data-testid="existing-logo-business-name"
                        maxLength={120}
                        autoComplete="organization"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="bi-business-description"
                        className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#D1D1D1] mb-2"
                      >
                        Short description of the business
                        <span className="ml-2 text-[10px] font-normal tracking-normal text-[#7A7A7A] normal-case">
                          Optional
                        </span>
                      </label>
                      <textarea
                        id="bi-business-description"
                        rows={3}
                        value={businessDescription}
                        onChange={(e) => setBusinessDescription(e.target.value)}
                        placeholder="What the business does, who it serves — 1–2 sentences."
                        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF6600] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#5A5A5A] outline-none transition-colors resize-y min-h-[72px]"
                        data-testid="existing-logo-business-description"
                        maxLength={600}
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Step 3 — Contextual BI upsell (Refresh + Vectorization only) */}
            {canOfferBiUpsell && (
              <Reveal>
                <button
                  type="button"
                  onClick={() => setAddBiUpsell((v) => !v)}
                  data-testid="existing-logo-bi-upsell-toggle"
                  className={`w-full text-left rounded-2xl p-5 border mb-8 flex items-start gap-4 transition-colors ${
                    addBiUpsell
                      ? "bg-[#1a1004] border-[#FF6600]"
                      : "bg-[#151515] border-[#262626] hover:border-[#FF6600]/40"
                  }`}
                >
                  <span
                    className={`w-6 h-6 grid place-items-center rounded-full border shrink-0 mt-0.5 ${
                      addBiUpsell
                        ? "bg-[#FF6600] border-[#FF6600] text-white"
                        : "border-[#3A3A3A] text-transparent"
                    }`}
                    aria-hidden
                  >
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#FF6600]">Optional add-on</span>
                    </div>
                    <div className="font-bold mt-0.5">
                      Build a complete identity around your {selected.key === "refresh" ? "refreshed" : "recreated"} logo
                    </div>
                    <p className="text-sm text-[#D1D1D1] mt-1 leading-relaxed">
                      Add Brand Identity — Existing Logo to this order: brand voice, color palette, typography, basic guidelines, business card, letterhead and email signature.
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-black text-lg">+{listMoney(biExistingItem?.price || 0, biExistingItem?.price_usd)}</div>
                    <div className="text-[10px] text-[#A3A3A3]">{usdApprox(biExistingItem?.price || 0)}</div>
                  </div>
                </button>
              </Reveal>
            )}

            {/* Step 4 — Review + total + CTA */}
            <Reveal>
              <div className="bg-[#151515] rounded-2xl border border-[#262626] p-6 sm:p-8" data-testid="existing-logo-review">
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF6600] mb-3">Your Selection</div>
                <div className="divide-y divide-[#262626]">
                  <div className="flex items-start justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <div className="font-semibold">{selected.item.name}</div>
                      <div className="text-xs text-[#A3A3A3]">{selected.tag}</div>
                    </div>
                    <div className="font-display font-bold shrink-0">
                      {money(selected.item.price)}
                    </div>
                  </div>
                  {canOfferBiUpsell && addBiUpsell && (
                    <div className="flex items-start justify-between gap-4 py-3" data-testid="existing-logo-review-bi">
                      <div className="min-w-0">
                        <div className="font-semibold">{biExistingItem.name}</div>
                        <div className="text-xs text-[#A3A3A3]">Contextual addition</div>
                      </div>
                      <div className="font-display font-bold shrink-0">
                        +{money(biExistingItem.price)}
                      </div>
                    </div>
                  )}
                  {isBiExisting && trimmedBusinessName && (
                    <div
                      className="flex items-start justify-between gap-4 py-3"
                      data-testid="existing-logo-review-business-name"
                    >
                      <div className="text-xs uppercase tracking-[0.16em] text-[#A3A3A3] font-bold shrink-0 pt-0.5">
                        Business
                      </div>
                      <div className="text-sm text-white text-right min-w-0 break-words">
                        {trimmedBusinessName}
                      </div>
                    </div>
                  )}
                  {isBiExisting && trimmedBusinessDescription && (
                    <div
                      className="flex items-start justify-between gap-4 py-3"
                      data-testid="existing-logo-review-business-description"
                    >
                      <div className="text-xs uppercase tracking-[0.16em] text-[#A3A3A3] font-bold shrink-0 pt-0.5">
                        Description
                      </div>
                      <div className="text-sm text-[#D1D1D1] text-right min-w-0 break-words whitespace-pre-wrap max-w-[70%]">
                        {trimmedBusinessDescription}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-5 pt-5 border-t border-[#262626] space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[#A3A3A3]">Subtotal</span>
                    <span data-testid="existing-logo-subtotal" className="font-semibold text-white tabular-nums">{money(total, totalUsd)}</span>
                  </div>
                  {taxRate > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#A3A3A3]" data-testid="existing-logo-tax-label">{taxLabel}</span>
                      <span data-testid="existing-logo-tax-amount" className="font-semibold text-white tabular-nums">{money(taxAmount, totalUsd != null ? totalUsd * taxRate : undefined)}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-[#262626] flex items-center justify-between">
                    <span className="text-xs text-[#A3A3A3] uppercase tracking-[0.16em] font-bold">Estimated Total</span>
                    <div className="text-right">
                      <div data-testid="existing-logo-total" className="font-display font-black text-3xl">{money(totalWithTax, totalUsd != null ? totalUsd * (1 + taxRate) : undefined)}</div>
                      <div className="text-xs text-[#A3A3A3]">{usdApprox(totalWithTax)}</div>
                    </div>
                  </div>
                  <ChargeDisclosure awgTotal={totalWithTax} className="pt-1" testId="existing-logo-charge-disclosure" />
                </div>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={proceed}
                    disabled={!canProceed || submitting}
                    className="bg-[#FF6600] hover:bg-[#e65a00] text-white font-bold flex-1"
                    data-testid="existing-logo-cta"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {!upload
                      ? "Upload logo to continue"
                      : isBiExisting && !trimmedBusinessName
                        ? "Add business name to continue"
                        : "Add to Cart & Checkout"}
                    {canProceed && !submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
                {!upload && (
                  <p className="text-[11px] text-[#A3A3A3] mt-3 text-center">
                    Upload your existing logo above to complete the request.
                  </p>
                )}
                {upload && isBiExisting && !trimmedBusinessName && (
                  <p
                    className="text-[11px] text-[#A3A3A3] mt-3 text-center"
                    data-testid="existing-logo-business-name-hint"
                  >
                    Enter the Business / Brand Name above to complete the request.
                  </p>
                )}
              </div>
            </Reveal>
          </>
        )}
        <ServiceHelpLinks />
      </section>
  );
};

export default ExistingLogoConfigurator;
